// @ts-check
/** @typedef {import('../types/api').Product} Product */
/** @typedef {import('../types/api').Collection} Collection */
/** @typedef {import('../types/api').ProductImage} ProductImage */
/** @typedef {import('../types/api').ProductVariant} ProductVariant */
/** @typedef {import('../types/api').InstagramPost} InstagramPost */

import {
  ExternalLink,
  Heart,
  Instagram,
  Layers,
  Link2,
  Loader2,
  Lock,
  Ruler,
  Sparkles,
  Tag,
  Unlink,
  X,
} from 'lucide-react';
import React, { Component, useCallback, useEffect, useRef, useState } from 'react';
import adminCollectionService from '../services/adminCollectionService';
import {
  adminProductAiService,
  adminProductImageService,
  adminProductVariantService,
} from '../services/adminProductExtrasService';
import adminProductService from '../services/adminProductService';
import postMarketingService from '../services/postMarketingService';
import api from '../utils/apiClient';
import { fashionModelFormFields } from '../utils/fashionModel';
import { changedImageFields, imageSnapshotById } from '../utils/imageChanges';
import ProductCompositionManager from './ProductCompositionManager';
import ProductGarmentMeasurementsManager from './ProductGarmentMeasurementsManager';
import ProductImagesManager from './ProductImagesManager';
import ProductRelatedManager from './ProductRelatedManager';
import ProductSimilarPinsManager from './ProductSimilarPinsManager';
import ProductTrustPillarsManager from './ProductTrustPillarsManager';
import ProductVariantsManager from './ProductVariantsManager';
import Modal from './ui/Modal';

const ProductEditModal = ({ productId, onClose, onSave }) => {
  const isNewProduct = !productId;
  const [savedAsDraft, setSavedAsDraft] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(productId);
  // Touched-since-load flag, reset after a successful save. setFormData below
  // is the wrapped setter every field handler already uses, so it marks
  // dirty automatically; loadProduct's initial populate uses the raw setter
  // instead so loading an existing product never itself counts as an edit.
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    name: '',
    slug: '',
    price: '',
    sale_price: '',
    description: '',
    short_description: '',
    primary_image: '',
    featured_image: '',
    badge: '',
    collection_id: '',
    product_type_id: '',
    additional_piece_type_ids: [],
    status: 'draft',
    is_featured: false,
    is_new_arrival: false,
    is_coming_soon: false,
    is_core_collection: false,
    is_bestseller: false,
    model_measurements: '',
    model_wearing_size: '',
    fashion_model_id: '',
    composition_shell_id: '', // New ID field
    composition_lining_id: '', // New ID field
    care_instructions: '',
    shipping_date: '',
    hsn_code: '',
    tax_rate: '',
    shipping_weight_kg: '',
    shipping_length_cm: '',
    shipping_breadth_cm: '',
    shipping_height_cm: '',
    similar_products_enabled: true,
    review_summary: '',
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  // Instagram linking state
  const [showPostPicker, setShowPostPicker] = useState(false);
  /** @type {[InstagramPost[], Function]} */
  const [allPosts, setAllPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  /** @type {[InstagramPost[], Function]} */
  const [linkedPosts, setLinkedPosts] = useState([]);
  const [loadingLinkedPosts, setLoadingLinkedPosts] = useState(false);
  const [linkingPostId, setLinkingPostId] = useState(null);

  // Taxonomy tagging state
  const [allEvents, setAllEvents] = useState([]);
  const [allMoods, setAllMoods] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedMoodIds, setSelectedMoodIds] = useState([]);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  // Tag picks are written on Save (see handleSubmit), not as they're clicked, so remember
  // they were touched: an untouched (or failed-to-load) selection is never written back.
  const tagsTouched = useRef(false);
  const toggleTag = (setIds, id, checked) => {
    tagsTouched.current = true;
    setIsDirty(true);
    setIds((prev) => (checked ? [...prev, id] : prev.filter((existing) => existing !== id)));
  };

  const isTabLocked = (tabName) => {
    if (!isNewProduct || savedAsDraft) return false;
    return tabName !== 'basic';
  };
  /** @type {[Collection[], Function]} */
  const [collections, setCollections] = useState([]);
  /** @type {[ProductImage[], Function]} */
  const [images, setImages] = useState([]);
  // What the server holds (alt_text + display_order) per image id, so save only PUTs images
  // that actually changed. Re-taken after every save.
  const savedImagesById = useRef({});
  /** @type {[ProductVariant[], Function]} */
  const [variants, setVariants] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // True once any Save has succeeded, so closing early still refreshes the products list behind us.
  const didSave = useRef(false);
  // The measurements manager registers its save-all function here; it stays mounted (hidden)
  // once first opened so unsaved grid edits survive tab switches.
  const measurementsSaveRef = useRef(/** @type {(() => Promise<void>) | null} */ (null));
  const measurementsOpened = useRef(false);

  const loadCollections = useCallback(async () => {
    try {
      const data = await adminCollectionService.getCollections();
      setCollections(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  }, []);

  const loadProduct = useCallback(async () => {
    try {
      const data = await adminProductService.getProduct(currentProductId);
      setFormDataRaw({
        name: data.name || '',
        slug: data.slug || '',
        price: data.price ? String(Number(data.price)) : '',
        sale_price: data.sale_price ? String(Number(data.sale_price)) : '',
        description: data.description || '',
        short_description: data.short_description || '',
        primary_image: data.primary_image || '',
        featured_image: data.featured_image || '',
        badge: data.badge || '',
        collection_id: data.collection_id || '',
        product_type_id: data.product_type_id || '',
        additional_piece_type_ids: data.additional_piece_type_ids || [],
        status: data.status || 'draft',
        is_featured: data.is_featured ?? false,
        is_new_arrival: data.is_new_arrival ?? false,
        is_coming_soon: data.is_coming_soon ?? false,
        is_core_collection: data.is_core_collection ?? false,
        is_bestseller: data.is_bestseller ?? false,
        model_measurements: data.model_measurements || '',
        model_wearing_size: data.model_wearing_size || '',
        fashion_model_id: data.fashion_model_id || '',
        composition_shell_id: data.composition_shell_id || '',
        composition_lining_id: data.composition_lining_id || '',
        care_instructions: data.care_instructions || '',
        shipping_date: data.shipping_date || '',
        hsn_code: data.hsn_code || '',
        tax_rate: String(data.tax_rate ?? ''),
        shipping_weight_kg: String(data.shipping_weight_kg ?? ''),
        shipping_length_cm: String(data.shipping_length_cm ?? ''),
        shipping_breadth_cm: String(data.shipping_breadth_cm ?? ''),
        shipping_height_cm: String(data.shipping_height_cm ?? ''),
        similar_products_enabled: data.similar_products_enabled ?? true,
        review_summary: data.review_summary || '',
      });
    } catch (err) {
      setError('Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [currentProductId]);

  const loadProductImages = useCallback(async () => {
    try {
      const data = await adminProductImageService.getProductImages(currentProductId);
      setImages(data);
      savedImagesById.current = imageSnapshotById(data);
    } catch (err) {
      console.error('Failed to load product images:', err);
      setImages([]);
    }
  }, [currentProductId]);

  const loadProductVariants = useCallback(async () => {
    try {
      const data = await adminProductVariantService.getProductVariants(currentProductId);
      setVariants(data);
    } catch (err) {
      console.error('Failed to load product variants:', err);
      setVariants([]);
    }
  }, [currentProductId]);

  const loadTaxonomyData = useCallback(async () => {
    try {
      setTaxonomyLoading(true);
      const [events, moods, places, features] = await Promise.all([
        api.get('/api/admin/taxonomy/events'),
        api.get('/api/admin/taxonomy/moods'),
        api.get('/api/admin/taxonomy/places'),
        api.get('/api/admin/taxonomy/features'),
      ]);
      setAllEvents(Array.isArray(events) ? events : []);
      setAllMoods(Array.isArray(moods) ? moods : []);
      setAllPlaces(Array.isArray(places) ? places : []);
      setAllFeatures(Array.isArray(features) ? features : []);

      if (currentProductId) {
        const taxonomy = await adminProductService.getProductTaxonomy(currentProductId);
        setSelectedEventIds(taxonomy.event_ids || []);
        setSelectedMoodIds(taxonomy.mood_ids || []);
        setSelectedPlaceIds(taxonomy.place_ids || []);
        setSelectedFeatureIds(taxonomy.feature_ids || []);
      }
    } catch (err) {
      console.error('Failed to load taxonomy data:', err);
    } finally {
      setTaxonomyLoading(false);
    }
  }, [currentProductId]);

  useEffect(() => {
    loadCollections();
    loadTaxonomyData();
    if (currentProductId) {
      loadProduct();
      loadProductImages();
      loadProductVariants();
    } else {
      setLoading(false);
    }
  }, [currentProductId, loadCollections, loadProduct, loadProductImages, loadProductVariants, loadTaxonomyData]);

  // Instagram linking helpers
  const loadLinkedPosts = async (prodId) => {
    try {
      setLoadingLinkedPosts(true);
      const data = await api.get(`/api/admin/posts/by-product/${prodId}`);
      setLinkedPosts(Array.isArray(data) ? data : []);
    } catch {
      setLinkedPosts([]);
    } finally {
      setLoadingLinkedPosts(false);
    }
  };

  const loadAllPosts = async () => {
    try {
      setLoadingPosts(true);
      const data = await postMarketingService.getPosts();
      setAllPosts(Array.isArray(data.items) ? data.items : []);
    } catch {
      setAllPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleLinkPost = async (postId) => {
    const prodId = currentProductId;
    if (!prodId) return;
    try {
      setLinkingPostId(postId);
      await api.patch(`/api/admin/posts/${postId}/link-product`, { product_id: prodId });
      await loadLinkedPosts(prodId);
      setShowPostPicker(false);
    } catch (err) {
      console.error('Failed to link post:', err);
    } finally {
      setLinkingPostId(null);
    }
  };

  const handleUnlinkPost = async (postId) => {
    const prodId = currentProductId;
    if (!prodId) return;
    try {
      setLinkingPostId(postId);
      await api.patch(`/api/admin/posts/${postId}/link-product`, { product_id: null });
      await loadLinkedPosts(prodId);
    } catch (err) {
      console.error('Failed to unlink post:', err);
    } finally {
      setLinkingPostId(null);
    }
  };

  // X button and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose. The internal onClose() call after a full,
  // successful save (end of handleSubmit) bypasses this deliberately.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    if (didSave.current) onSave();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      slug: currentProductId ? prev.slug : generateSlug(name),
    }));
  };

  const setFeaturedImage = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      featured_image: imageUrl,
    }));
  };

  const TAB_ORDER = [
    'basic',
    'images',
    'variants',
    'measurements',
    'composition',
    'tags',
    'related',
    'similar',
    'trust_pillars',
    'instagram',
  ];
  const isLastTab = activeTab === TAB_ORDER[TAB_ORDER.length - 1];
  if (activeTab === 'measurements') measurementsOpened.current = true;

  const goToNextTab = () => {
    const currentIdx = TAB_ORDER.indexOf(activeTab);
    if (currentIdx < TAB_ORDER.length - 1) {
      const nextTab = TAB_ORDER[currentIdx + 1];
      if (nextTab === 'instagram' && currentProductId) {
        loadLinkedPosts(currentProductId);
      }
      setActiveTab(nextTab);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        sale_price: formData.sale_price || null,
        collection_id: formData.collection_id || null,
        product_type_id: formData.product_type_id || null,
        composition_shell_id: formData.composition_shell_id || null,
        composition_lining_id: formData.composition_lining_id || null,
        fashion_model_id: formData.fashion_model_id || null,
        primary_image: formData.primary_image || null,
        featured_image: formData.featured_image || null,
        // Empty number inputs come back as '' — send null so the backend's Decimal
        // fields accept them instead of failing validation.
        tax_rate: formData.tax_rate === '' ? null : formData.tax_rate,
        shipping_weight_kg: formData.shipping_weight_kg === '' ? null : formData.shipping_weight_kg,
        shipping_length_cm: formData.shipping_length_cm === '' ? null : formData.shipping_length_cm,
        shipping_breadth_cm: formData.shipping_breadth_cm === '' ? null : formData.shipping_breadth_cm,
        shipping_height_cm: formData.shipping_height_cm === '' ? null : formData.shipping_height_cm,
      };

      let savedProductId = currentProductId;

      if (currentProductId) {
        await adminProductService.updateProduct(currentProductId, submitData);
      } else {
        // New product — create as draft first
        const newProduct = await adminProductService.createProduct({
          ...submitData,
          status: 'draft',
          is_published: false,
        });
        savedProductId = newProduct.id;
        setCurrentProductId(savedProductId);
        setSavedAsDraft(true);
      }
      didSave.current = true;

      // Write whatever is pending on ANY tab, not just the visible one: image edits, tag picks
      // and measurement grids live in modal/manager state, so saving only the active tab would
      // silently drop edits made before switching tabs.
      // Variants are deliberately absent: ProductVariantsManager persists every add/edit/delete
      // as it happens, and re-PUTting the snapshot loaded when the modal opened would overwrite
      // stock and availability changed since (e.g. the last unit selling).
      if (savedProductId) {
        await saveProductImages(savedProductId);
        if (tagsTouched.current) {
          await adminProductService.updateProductTaxonomy(savedProductId, {
            event_ids: selectedEventIds,
            mood_ids: selectedMoodIds,
            place_ids: selectedPlaceIds,
            feature_ids: selectedFeatureIds,
          });
          tagsTouched.current = false;
        }
        await measurementsSaveRef.current?.();
      }

      // Everything pending on every tab has just been persisted.
      setIsDirty(false);

      if (!isLastTab) {
        setSaving(false);
        goToNextTab();
        return;
      }

      // Last tab — final save and close
      onSave();
      onClose();
    } catch (err) {
      // apiClient throws Error(<server detail>), so the real reason (duplicate slug, 422, ...) is err.message.
      setError(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const saveProductImages = async (prodId) => {
    let wrote = false;
    for (const image of images) {
      if (image.id.toString().startsWith('temp_')) {
        const created = await adminProductImageService.addProductImage(prodId, {
          image_url: image.url,
          alt_text: image.alt_text,
          display_order: image.display_order,
          focal_x: image.focal_x ?? null,
          focal_y: image.focal_y ?? null,
        });
        wrote = true;
        // Swap the temp entry for the saved record straight away: if a later write fails, the
        // next Save must not POST this upload a second time.
        setImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, ...created } : img)));
        Object.assign(savedImagesById.current, imageSnapshotById([created]));
      } else {
        // Alt text edits and up/down reorders (display_order) both land here.
        const changes = changedImageFields(image, savedImagesById.current[image.id]);
        if (changes) {
          await adminProductImageService.updateProductImage(prodId, image.id, changes);
          wrote = true;
          Object.assign(savedImagesById.current, imageSnapshotById([image]));
        }
      }
    }
    if (!wrote) return;

    // Re-read from the server: new uploads get their real ids (otherwise the next Save
    // would POST them a second time) and the saved snapshot matches what was just written.
    const saved = await adminProductImageService.getProductImages(prodId);
    setImages(saved);
    savedImagesById.current = imageSnapshotById(saved);
  };

  if (loading) {
    return (
      <Modal maxWidth="4xl" closeOnBackdrop={false} className="rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-card sticky top-0 z-10">
          <h2 className="text-2xl font-serif">{currentProductId ? 'Edit Product' : 'Add Product'}</h2>
        </div>

        {/* Loading Content */}
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-foreground mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading product details...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal maxWidth="6xl" closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center z-10">
        <h2 className="text-2xl font-serif">
          {currentProductId ? 'Edit Product' : 'Add New Product'}
          {savedAsDraft && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">(Draft saved - complete other tabs)</span>
          )}
        </h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          <X size={24} />
        </button>
      </div>

      <div className="sticky top-[73px] bg-card border-b border-border px-6 z-10 overflow-x-auto">
        <div className="flex gap-6 w-max min-w-full">
          {[
            { key: 'basic', label: 'Basic Information' },
            { key: 'images', label: `Images (${images.length})` },
            { key: 'variants', label: `Variants (${variants.length})` },
            { key: 'measurements', label: 'Measurements', icon: <Ruler size={14} /> },
            { key: 'composition', label: 'Composition', icon: <Layers size={14} /> },
            { key: 'tags', label: 'Tags', icon: <Tag size={14} /> },
            { key: 'related', label: 'Complete the Look' },
            { key: 'similar', label: 'More Like This' },
            { key: 'trust_pillars', label: 'Extra Trust Pillars' },
            { key: 'instagram', label: 'Instagram', icon: <Instagram size={14} /> },
          ].map((tab) => {
            const locked = isTabLocked(tab.key);
            return (
              <button
                key={tab.key}
                onClick={() => {
                  if (locked) return;
                  if (tab.key === 'instagram' && currentProductId) {
                    loadLinkedPosts(currentProductId);
                  }
                  setActiveTab(tab.key);
                }}
                disabled={locked}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${
                  locked
                    ? 'opacity-40 cursor-not-allowed border-transparent text-muted-foreground/70'
                    : activeTab === tab.key
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {locked && <Lock size={12} className="mr-1" />}
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/50 border rounded-lg border-red-200 dark:border-red-900 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <TabErrorBoundary>
          <div style={{ display: activeTab === 'basic' ? 'block' : 'none' }}>
            <BasicInformationTab
              formData={formData}
              setFormData={setFormData}
              collections={collections}
              images={images}
              handleChange={handleChange}
              handleNameChange={handleNameChange}
              productId={currentProductId}
            />
          </div>

          <div style={{ display: activeTab === 'images' ? 'block' : 'none' }}>
            <div className="py-4">
              <ProductImagesManager
                productId={currentProductId}
                productName={formData.name}
                images={images}
                onImagesChange={(next) => {
                  setIsDirty(true);
                  setImages(next);
                }}
                featuredImageUrl={formData.featured_image}
                setFeaturedImage={setFeaturedImage}
                variants={variants}
              />
            </div>
          </div>

          <div style={{ display: activeTab === 'variants' ? 'block' : 'none' }}>
            <div className="py-4">
              <ProductVariantsManager
                productId={currentProductId}
                productSlug={formData.slug}
                variants={variants}
                onVariantsChange={setVariants}
                productImages={images}
              />
            </div>
          </div>

          {/* Mounted on first visit, then only hidden, so unsaved grid edits survive tab switches. */}
          {measurementsOpened.current && (
            <div className="py-4" style={{ display: activeTab === 'measurements' ? 'block' : 'none' }}>
              <ProductGarmentMeasurementsManager
                productId={currentProductId}
                variants={variants}
                additionalPieceTypeIds={formData.additional_piece_type_ids}
                onAdditionalPieceTypeIdsChange={(ids) =>
                  setFormData((prev) => ({ ...prev, additional_piece_type_ids: ids }))
                }
                onDirty={() => setIsDirty(true)}
                saveHandlerRef={measurementsSaveRef}
              />
            </div>
          )}

          {activeTab === 'composition' && (
            <div className="py-4">
              <ProductCompositionManager
                productId={currentProductId}
                additionalPieceTypeIds={formData.additional_piece_type_ids}
                onAdditionalPieceTypeIdsChange={(ids) =>
                  setFormData((prev) => ({ ...prev, additional_piece_type_ids: ids }))
                }
                mainShellCompositionId={formData.composition_shell_id}
                mainLiningCompositionId={formData.composition_lining_id}
                handleChange={handleChange}
              />
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="py-4">
              {taxonomyLoading ? (
                <div className="text-center py-8 text-muted-foreground/70 text-sm">Loading tags...</div>
              ) : (
                <div className="space-y-6">
                  {/* Occasions */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Occasions</h4>
                    {allEvents.length === 0 ? (
                      <p className="text-sm text-muted-foreground/70">No occasions available.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {allEvents.map((event) => (
                          <label key={event.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedEventIds.includes(event.id)}
                              onChange={(e) => toggleTag(setSelectedEventIds, event.id, e.target.checked)}
                              className="rounded-lg border-input"
                            />
                            <span>{event.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Moods */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Moods</h4>
                    {allMoods.length === 0 ? (
                      <p className="text-sm text-muted-foreground/70">No moods available.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {allMoods.map((mood) => (
                          <label key={mood.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedMoodIds.includes(mood.id)}
                              onChange={(e) => toggleTag(setSelectedMoodIds, mood.id, e.target.checked)}
                              className="rounded-lg border-input"
                            />
                            <span>{mood.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Places */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Places</h4>
                    {allPlaces.length === 0 ? (
                      <p className="text-sm text-muted-foreground/70">No places available.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {allPlaces.map((place) => (
                          <label key={place.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPlaceIds.includes(place.id)}
                              onChange={(e) => toggleTag(setSelectedPlaceIds, place.id, e.target.checked)}
                              className="rounded-lg border-input"
                            />
                            <span>{place.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Features (Dress Attributes)</h4>
                    {allFeatures.length === 0 ? (
                      <p className="text-sm text-muted-foreground/70">No features available.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {allFeatures.map((feature) => (
                          <label key={feature.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedFeatureIds.includes(feature.id)}
                              onChange={(e) => toggleTag(setSelectedFeatureIds, feature.id, e.target.checked)}
                              className="rounded-lg border-input"
                            />
                            <span>{feature.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && (
            <div className="py-4">
              <ProductRelatedManager productId={currentProductId} />
            </div>
          )}

          {activeTab === 'similar' && (
            <div className="py-4">
              <ProductSimilarPinsManager productId={currentProductId} />
            </div>
          )}

          {activeTab === 'trust_pillars' && (
            <div className="py-4">
              <ProductTrustPillarsManager productId={currentProductId} />
            </div>
          )}

          {activeTab === 'instagram' && (
            <div className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-muted-foreground">Instagram posts linked to this product.</p>
                {currentProductId && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostPicker(true);
                      if (allPosts.length === 0) loadAllPosts();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    <Link2 size={14} />
                    Link Post
                  </button>
                )}
              </div>

              {/* Linked posts */}
              {loadingLinkedPosts ? (
                <div className="text-center py-8 text-muted-foreground/70 text-sm">Loading linked posts...</div>
              ) : linkedPosts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground/70">
                  <Instagram size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No Instagram posts linked to this product yet.</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Click "Link Post" to connect an Instagram post.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {linkedPosts.map((post, idx) => (
                    <div key={post.id || idx} className="relative aspect-square overflow-hidden rounded-lg group">
                      <a href={post.permalink || '#'} target="_blank" rel="noopener noreferrer">
                        {post.thumbnail_url ? (
                          <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : post.post_type === 'video' || post.post_type === 'reel' ? (
                          // No thumbnail on record — an .mp4 URL can't render in <img>.
                          <video
                            src={post.media_url}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                        )}
                      </a>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex items-center gap-3 text-white text-sm">
                          <div className="flex items-center gap-1">
                            <Heart size={14} fill="white" />
                            <span>{post.like_count || 0}</span>
                          </div>
                          <ExternalLink size={14} />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnlinkPost(post.id)}
                        disabled={linkingPostId === post.id}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 transition hover:bg-red-600 disabled:opacity-50"
                        title="Unlink post"
                      >
                        <Unlink size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Post picker modal */}
              {showPostPicker && (
                <Modal
                  maxWidth="lg"
                  zIndexClass="z-[60]"
                  onClose={() => setShowPostPicker(false)}
                  className="flex flex-col"
                >
                  <div className="px-4 py-3 border-b flex justify-between items-center">
                    <h4 className="text-sm font-medium">Select a post to link</h4>
                    <button
                      onClick={() => setShowPostPicker(false)}
                      className="text-muted-foreground/70 hover:text-foreground"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="overflow-y-auto flex-1 p-4">
                    {loadingPosts ? (
                      <div className="text-center py-8 text-muted-foreground/70 text-sm">Loading posts...</div>
                    ) : allPosts.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground/70 text-sm">
                        No synced posts available.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {allPosts
                          .filter((p) => !linkedPosts.some((lp) => lp.id === p.id))
                          .map((post) => (
                            <div
                              key={post.id}
                              className="flex items-center gap-3 p-2 border rounded-lg hover:bg-muted/50"
                            >
                              {post.thumbnail_url || post.media_url ? (
                                <img
                                  src={post.thumbnail_url || post.media_url}
                                  alt=""
                                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                  <Instagram size={16} className="text-muted-foreground/70" />
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground flex-1 line-clamp-2">
                                {post.caption
                                  ? post.caption.substring(0, 80) + (post.caption.length > 80 ? '...' : '')
                                  : 'No caption'}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleLinkPost(post.id)}
                                disabled={linkingPostId === post.id}
                                className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex-shrink-0"
                              >
                                {linkingPostId === post.id ? 'Linking...' : 'Link'}
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </Modal>
              )}
            </div>
          )}
        </TabErrorBoundary>

        {/* Sticky Footer with Cancel/Save buttons */}
        <div className="sticky bottom-0 bg-card border-t border-border px-4 sm:px-6 py-4 flex flex-wrap gap-3 justify-end mt-6 shadow-lg z-10">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-4 sm:px-6 py-2 border border-input hover:bg-muted/50 rounded-lg"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 sm:px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50 rounded-lg"
          >
            {saving
              ? 'Saving...'
              : isNewProduct && !savedAsDraft
                ? 'Save as Draft & Continue'
                : isLastTab
                  ? 'Save Product'
                  : 'Next'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Error boundary to catch and display crashes in product edit tabs
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[ProductEditModal] Tab crash:', error);
    console.error('[ProductEditModal] Component stack:', errorInfo.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg m-4">
          <h3 className="text-red-800 dark:text-red-300 font-medium mb-2">Something went wrong in this tab</h3>
          <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap bg-red-100 dark:bg-red-900/40 p-3 rounded-md overflow-auto max-h-40">
            {this.state.error?.message || 'Unknown error'}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-3 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const BasicInformationTab = ({
  formData,
  setFormData,
  collections,
  images,
  handleChange,
  handleNameChange,
  productId,
}) => {
  // Compositions catalog is only needed here for the AI description generator's
  // fact-gathering below — the shell/lining dropdowns themselves live in the
  // Composition tab (ProductCompositionManager) now.
  const [compositions, setCompositions] = React.useState([]);
  const [sizeOptions, setSizeOptions] = React.useState([]);
  const [fashionModels, setFashionModels] = useState([]);
  const [fashionModelsError, setFashionModelsError] = useState(false);
  const [loadingSizes, setLoadingSizes] = React.useState(true);
  const [badgeOptions, setBadgeOptions] = React.useState([]);
  const [loadingBadges, setLoadingBadges] = React.useState(true);
  const [productTypes, setProductTypes] = React.useState([]);
  const [generatingDescription, setGeneratingDescription] = React.useState(false);
  const [generateError, setGenerateError] = React.useState('');
  const [generatingReviewSummary, setGeneratingReviewSummary] = React.useState(false);
  const [reviewSummaryError, setReviewSummaryError] = React.useState('');

  // Fetch compositions, sizes, badges, and product types from DB
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [compositionsData, sizesData, badgesData, typesData] = await Promise.all([
          api.get('/api/admin/catalog/compositions'),
          api.get('/api/admin/catalog/size-variants'),
          api.get('/api/admin/catalog/badges'),
          api.get('/api/admin/taxonomy/product-types'),
        ]);

        setCompositions(compositionsData);
        setSizeOptions(sizesData);
        setBadgeOptions(badgesData.filter((b) => b.is_active));
        setProductTypes(typesData.filter((t) => t.is_active));
      } catch (error) {
        console.error('Error fetching catalog data:', error);
      } finally {
        setLoadingSizes(false);
        setLoadingBadges(false);
      }
    };

    fetchData();
  }, []);

  // Fashion models load separately so a failure here cannot empty the other dropdowns.
  React.useEffect(() => {
    api
      .get('/api/admin/fashion-models')
      .then(setFashionModels)
      .catch((error) => {
        console.error('Error fetching fashion models:', error);
        setFashionModelsError(true);
      });
  }, []);

  // Picking a model fills the measurements (and a blank wearing size); both stay editable.
  const handleFashionModelChange = (e) => {
    const model = fashionModels.find((m) => m.id === e.target.value) || null;
    setFormData((prev) => ({ ...prev, ...fashionModelFormFields(model, prev.model_wearing_size) }));
  };

  const handleGenerateDescription = async () => {
    if (!formData.name.trim()) {
      setGenerateError('Add a product name first.');
      return;
    }
    if (
      (formData.description || formData.short_description) &&
      !window.confirm('This will replace the current Description and Short Description text. Continue?')
    ) {
      return;
    }

    setGenerateError('');
    setGeneratingDescription(true);
    try {
      const productType = productTypes.find((t) => t.id === formData.product_type_id);
      const collection = collections.find((c) => c.id === formData.collection_id);
      const shellComposition = compositions.find((c) => c.id === formData.composition_shell_id);
      const liningComposition = compositions.find((c) => c.id === formData.composition_lining_id);
      const imageUrls = (images || [])
        .map((img) => img.url)
        .filter(Boolean)
        .slice(0, 4);

      const result = await adminProductAiService.generateDescription({
        name: formData.name,
        product_type: productType?.name,
        collection_name: collection?.name,
        composition_shell: shellComposition ? `${shellComposition.alias} (${shellComposition.name})` : undefined,
        composition_lining: liningComposition ? `${liningComposition.alias} (${liningComposition.name})` : undefined,
        care_instructions: formData.care_instructions,
        badge: formData.badge,
        image_urls: imageUrls,
      });

      handleChange({ target: { name: 'description', value: result.description || '' } });
      if (result.short_description) {
        handleChange({ target: { name: 'short_description', value: result.short_description } });
      }
    } catch (err) {
      setGenerateError(err.message || 'Failed to generate description');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleGenerateReviewSummary = async () => {
    if (!productId) {
      setReviewSummaryError('Save the product first — needs a saved product to look up its reviews.');
      return;
    }
    if (formData.review_summary && !window.confirm('This will replace the current review summary. Continue?')) {
      return;
    }

    setReviewSummaryError('');
    setGeneratingReviewSummary(true);
    try {
      const result = await adminProductAiService.generateReviewSummary(productId);
      handleChange({ target: { name: 'review_summary', value: result.review_summary || '' } });
    } catch (err) {
      setReviewSummaryError(err.message || 'Failed to generate review summary');
    } finally {
      setGeneratingReviewSummary(false);
    }
  };

  // Product types can carry default customs (HSN/tax) and parcel (weight/dims) values (set in
  // Taxonomy) so new products of that type don't need them retyped every time.
  // Only fills fields the admin hasn't already touched — never overwrites an existing value.
  const PRODUCT_TYPE_DEFAULT_FIELDS = [
    ['hsn_code', 'default_hsn_code'],
    ['tax_rate', 'default_tax_rate'],
    ['shipping_weight_kg', 'default_weight_kg'],
    ['shipping_length_cm', 'default_length_cm'],
    ['shipping_breadth_cm', 'default_breadth_cm'],
    ['shipping_height_cm', 'default_height_cm'],
  ];

  const handleProductTypeChange = (e) => {
    handleChange(e);
    const productType = productTypes.find((t) => t.id === e.target.value);
    if (!productType) return;
    for (const [formField, defaultField] of PRODUCT_TYPE_DEFAULT_FIELDS) {
      if (formData[formField] === '' && productType[defaultField] != null) {
        handleChange({ target: { name: formField, value: String(productType[defaultField]) } });
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="md:col-span-2">
        <h3 className="text-lg font-medium mb-4">Basic Information</h3>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleNameChange}
          required
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Slug <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="slug"
          value={formData.slug}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Price (MRP in ₹) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          step="0.01"
          name="price"
          value={formData.price}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Sale Price (₹, optional)</label>
        <input
          type="number"
          step="0.01"
          name="sale_price"
          value={formData.sale_price}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          HSN Code
          {!formData.hsn_code && (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
              Required for international shipping
            </span>
          )}
        </label>
        <input
          type="text"
          name="hsn_code"
          value={formData.hsn_code || ''}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-foreground ${
            formData.hsn_code ? 'border-input' : 'border-amber-400 bg-amber-50 dark:bg-amber-950/50'
          }`}
          placeholder="e.g., 6204"
          maxLength={20}
        />
        {!formData.hsn_code && (
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
            Carriers reject customs declarations without an HS code, so international labels for this product will fail
            until it&apos;s filled in.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Tax Rate (%)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          name="tax_rate"
          value={formData.tax_rate}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          placeholder="e.g., 5"
        />
        <p className="text-xs text-muted-foreground mt-1">Per-item tax percentage sent on the customs declaration.</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Collection</label>
        <select
          name="collection_id"
          value={formData.collection_id}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        >
          <option value="">No Collection</option>
          {collections.map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Product Type</label>
        <select
          name="product_type_id"
          value={formData.product_type_id}
          onChange={handleProductTypeChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        >
          <option value="">Select Type</option>
          {productTypes.map((pt) => (
            <option key={pt.id} value={pt.id}>
              {pt.name}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-1">Short Description</label>
        <input
          type="text"
          name="short_description"
          value={formData.short_description}
          onChange={handleChange}
          maxLength={500}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
      </div>

      <div className="md:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <label className="block text-sm font-medium">Description</label>
          <button
            type="button"
            onClick={handleGenerateDescription}
            disabled={generatingDescription}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
            title="Generate description &amp; short description from the product name, details, and uploaded images"
          >
            {generatingDescription ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {generatingDescription ? 'Generating...' : 'Generate with AI'}
          </button>
        </div>
        {generateError && <p className="text-xs text-red-600 dark:text-red-400 mb-1">{generateError}</p>}
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
        <p className="text-xs text-muted-foreground mt-1">
          AI drafts fill in both Description and Short Description above — review before saving; it only knows what's
          filled in here plus any uploaded images, so double-check fabric and fit claims.
        </p>
      </div>

      <div className="md:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <label className="block text-sm font-medium">Review Summary</label>
          <button
            type="button"
            onClick={handleGenerateReviewSummary}
            disabled={generatingReviewSummary}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50"
            title="Summarize what customers have praised, from this product's approved reviews"
          >
            {generatingReviewSummary ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {generatingReviewSummary ? 'Generating...' : formData.review_summary ? 'Regenerate' : 'Generate with AI'}
          </button>
        </div>
        {reviewSummaryError && <p className="text-xs text-red-600 dark:text-red-400 mb-1">{reviewSummaryError}</p>}
        <textarea
          name="review_summary"
          value={formData.review_summary}
          onChange={handleChange}
          rows={2}
          placeholder="Generated from approved customer reviews — shown as a small, understated note on the product page."
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Drawn only from this product's approved reviews. You can hand-edit the wording after generating.
        </p>
      </div>

      <div className="md:col-span-2">
        <h3 className="text-lg font-medium mb-4 mt-4">Product Details</h3>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Badge</label>
        {loadingBadges ? (
          <div className="w-full px-3 py-2 border border-input rounded-lg text-sm text-muted-foreground/70">
            Loading badges...
          </div>
        ) : badgeOptions.length > 0 ? (
          <select
            name="badge"
            value={formData.badge}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          >
            <option value="">No Badge</option>
            {badgeOptions.map((badge) => (
              <option key={badge.id} value={badge.name}>
                {badge.icon} {badge.name} - {badge.display_text}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            name="badge"
            value={formData.badge}
            onChange={handleChange}
            placeholder="e.g., NEW, SALE"
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Shipping Date</label>
        <input
          type="date"
          name="shipping_date"
          value={formData.shipping_date}
          onChange={handleChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        />
        <p className="text-xs text-muted-foreground mt-1">Estimated shipping date for this product</p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-1">Parcel Weight &amp; Dimensions</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Weight (kg)</label>
            <input
              type="number"
              step="0.001"
              min="0"
              name="shipping_weight_kg"
              value={formData.shipping_weight_kg}
              onChange={handleChange}
              placeholder="1.250"
              className="w-full px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Length (cm)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="shipping_length_cm"
              value={formData.shipping_length_cm}
              onChange={handleChange}
              placeholder="35"
              className="w-full px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Breadth (cm)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="shipping_breadth_cm"
              value={formData.shipping_breadth_cm}
              onChange={handleChange}
              placeholder="25"
              className="w-full px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Height (cm)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="shipping_height_cm"
              value={formData.shipping_height_cm}
              onChange={handleChange}
              placeholder="10"
              className="w-full px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Packed parcel measurements. Carriers bill on the greater of dead weight and volumetric weight, so filling
          these in avoids billing disputes — and they become mandatory once the carrier API integration goes live.
        </p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-1">Fashion Model</label>
        <select
          name="fashion_model_id"
          value={formData.fashion_model_id}
          onChange={handleFashionModelChange}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        >
          <option value="">Select model (optional)</option>
          {fashionModels
            .filter((m) => m.is_active || m.id === formData.fashion_model_id)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.is_active ? '' : ' (inactive)'}
              </option>
            ))}
        </select>
        {fashionModelsError && <p className="text-xs text-muted-foreground mt-1">Could not load models</p>}
        <p className="text-xs text-muted-foreground mt-1">
          Fills the measurements and wearing size below; both stay editable. Manage models in Catalog Settings.
        </p>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-1">Model Measurements</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Height</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="5'8&quot;"
                value={formData.model_measurements?.split(',')[0]?.split(':')[1]?.trim() || ''}
                onChange={(e) => {
                  const measurements = formData.model_measurements?.split(',') || ['', '', '', ''];
                  measurements[0] = `Height: ${e.target.value}`;
                  handleChange({ target: { name: 'model_measurements', value: measurements.join(', ') } });
                }}
                className="flex-1 px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Bust</label>
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="34"
                value={
                  formData.model_measurements
                    ?.split(',')[1]
                    ?.split(':')[1]
                    ?.trim()
                    .replace(/[^0-9.]/g, '') || ''
                }
                onChange={(e) => {
                  const measurements = formData.model_measurements?.split(',') || ['', '', '', ''];
                  measurements[1] = `Bust: ${e.target.value.replace(/[^0-9.]/g, '')}`;
                  handleChange({ target: { name: 'model_measurements', value: measurements.join(', ') } });
                }}
                className="w-16 px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
              />
              <span className="text-sm text-muted-foreground py-1">in</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Waist</label>
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="26"
                value={
                  formData.model_measurements
                    ?.split(',')[2]
                    ?.split(':')[1]
                    ?.trim()
                    .replace(/[^0-9.]/g, '') || ''
                }
                onChange={(e) => {
                  const measurements = formData.model_measurements?.split(',') || ['', '', '', ''];
                  measurements[2] = `Waist: ${e.target.value.replace(/[^0-9.]/g, '')}`;
                  handleChange({ target: { name: 'model_measurements', value: measurements.join(', ') } });
                }}
                className="w-16 px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
              />
              <span className="text-sm text-muted-foreground py-1">in</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Hips</label>
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="36"
                value={
                  formData.model_measurements
                    ?.split(',')[3]
                    ?.split(':')[1]
                    ?.trim()
                    .replace(/[^0-9.]/g, '') || ''
                }
                onChange={(e) => {
                  const measurements = formData.model_measurements?.split(',') || ['', '', '', ''];
                  measurements[3] = `Hips: ${e.target.value.replace(/[^0-9.]/g, '')}`;
                  handleChange({ target: { name: 'model_measurements', value: measurements.join(', ') } });
                }}
                className="w-16 px-2 py-1 text-sm border border-input rounded-lg focus:outline-none focus:border-foreground"
              />
              <span className="text-sm text-muted-foreground py-1">in</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Model's measurements for reference</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Model Wearing Size</label>
        {loadingSizes ? (
          <div className="w-full px-3 py-2 border border-input rounded-lg text-sm text-muted-foreground/70">
            Loading sizes...
          </div>
        ) : sizeOptions.length > 0 ? (
          <select
            name="model_wearing_size"
            value={formData.model_wearing_size}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          >
            <option value="">Select Size</option>
            {sizeOptions.map((size) => (
              <option key={size.id} value={size.primary_name}>
                {size.primary_name}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            name="model_wearing_size"
            value={formData.model_wearing_size}
            onChange={handleChange}
            placeholder="Enter size (e.g., S, M, L)"
            className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        )}
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium mb-1">Care Instructions</label>
        <textarea
          name="care_instructions"
          value={formData.care_instructions}
          readOnly
          rows={3}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground bg-muted cursor-not-allowed"
          placeholder="Care instructions will be auto-populated based on shell composition, set in the Composition tab"
        />
      </div>

      <div className="md:col-span-2 mt-4">
        <div className="flex items-center gap-6">
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              name="status"
              value={formData.status || 'draft'}
              onChange={handleChange}
              className="px-3 py-2 border border-input focus:outline-none focus:border-foreground rounded-lg"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-5">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_featured" className="text-sm font-medium">
                Featured
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_new_arrival"
                name="is_new_arrival"
                checked={formData.is_new_arrival}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_new_arrival" className="text-sm font-medium">
                New Arrival
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_coming_soon"
                name="is_coming_soon"
                checked={formData.is_coming_soon}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_coming_soon" className="text-sm font-medium">
                Coming Soon
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_core_collection"
                name="is_core_collection"
                checked={formData.is_core_collection}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_core_collection" className="text-sm font-medium">
                Core Collection
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_bestseller"
                name="is_bestseller"
                checked={formData.is_bestseller}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_bestseller" className="text-sm font-medium">
                Bestseller
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="similar_products_enabled"
                name="similar_products_enabled"
                checked={formData.similar_products_enabled}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="similar_products_enabled" className="text-sm font-medium">
                Show "More Like This" on product page
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductEditModal;
